import { useMemo } from "react";
import { isAllowed, type Decision } from "../../../api/access/decisions";
import { chosenProjectDecision, useProjects } from "../../../projects/projectContext";
import type { RunIntent } from "./threadIntent";
import { useThreadPreparation } from "./useThreadPreparation";

/**
 * Whether a new Thread can start in the chosen project: yes, not known while the project
 * loads, unknown because reading it failed, or why not. The DMW core is read-only for now by its
 * own project data (F107); anywhere else New Thread needs the server's start permission for a
 * Workflow, `propose_change`.
 */
export type NewThreadStatus = "may" | "checking" | "failed" | "coreReadOnly" | "notPermitted";

export interface NewThread {
  readonly status: NewThreadStatus;
  /** The served decision on starting one (`propose_change`), which says why where it's refused (GS-8). */
  readonly decision: Decision;
  /** Opens the Thread view in preparation, where the Workflow is chosen (WA-11, F110). */
  readonly start: () => void;
  /** The same, in place of the current page, as `/threads` becomes a new Thread's draft (F108). */
  readonly startInPlace: () => void;
}

/**
 * The one entry for a new Thread of the chosen project (F107, F110): the sidebar's New Thread
 * and `/threads` both start here and both say why when it can't. The core is read-only because
 * it is the installation's own project (`installationOwned`, ADR-0037 D5), not because of a role.
 */
export function useNewThread(projectId: string | null): NewThread {
  const projects = useProjects();
  const preparation = useThreadPreparation();
  const chosen = projects.selectedProject;
  const decision = chosenProjectDecision(projects, "propose_change");
  const status: NewThreadStatus =
    projectId === null || chosen === null || chosen.status === "loading"
      ? "checking"
      : chosen.status === "failed"
        ? "failed"
        : chosen.value.project.installationOwned
        ? "coreReadOnly"
        : isAllowed(decision)
          ? "may"
          : "notPermitted";
  return useMemo(() => {
    const intent: RunIntent | null = projectId === null ? null : { kind: "run", projectId, returnTo: null, workflow: null, entries: [], corpusVersion: null };
    return {
      status,
      decision,
      start: () => {
        if (intent !== null) preparation.prepare(intent);
      },
      startInPlace: () => {
        if (intent !== null) preparation.prepare(intent, { inPlace: true });
      },
    };
  }, [status, decision, projectId, preparation]);
}
